import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import instituteService from "../../services/instituteService";
import { queryKeys } from "./queryKeys";

export const useInstitutes = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: queryKeys.institutes.list(filters),
    queryFn: async () => {
      const res = await instituteService.getInstitutes(filters);
      // Return raw response payload so callers can inspect data/pagination as needed
      return res.data;
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    ...options,
  });
};

export const useInstitute = (id) => {
  return useQuery({
    queryKey: queryKeys.institutes.detail(id),
    queryFn: async () => {
      const res = await instituteService.getInstituteById(id);
      return res.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useInstituteDepartments = (instituteId) => {
  return useQuery({
    queryKey: [...queryKeys.institutes.detail(instituteId), "departments"],
    queryFn: async () => {
      const res = await instituteService.getInstituteDepartments(instituteId);
      return res.data;
    },
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateInstitute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (instituteData) => {
      const res = await instituteService.createInstitute(instituteData);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.institutes.lists(),
      });
    },
  });
};

export const useUpdateInstitute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, instituteData }) => {
      const res = await instituteService.updateInstitute(id, instituteData);
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.institutes.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.institutes.lists(),
      });
    },
  });
};

export const useDeleteInstitute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await instituteService.deleteInstitute(id);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.institutes.lists(),
      });
    },
  });
};

export const useUpdateInstituteStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await instituteService.updateInstituteStatus(id, status);
      return res.data;
    },
    onSuccess: (data, variables) => {
      const { id, status } = variables;
      queryClient.invalidateQueries({
        queryKey: queryKeys.institutes.detail(id),
      });
      try {
        queryClient.setQueriesData(
          { queryKey: queryKeys.institutes.lists(), exact: false },
          (old) => {
            if (old == null || typeof old !== "object") return old;
            const data = old.data;
            const list = Array.isArray(data) ? data : (data && data.institutes);
            if (!Array.isArray(list)) return old;
            const nextList = list.map((inst) =>
              inst && inst.id === id ? { ...inst, status } : inst
            );
            if (Array.isArray(data)) return { ...old, data: nextList };
            return { ...old, data: { ...(data || {}), institutes: nextList } };
          }
        );
      } catch (e) {
        // ignore cache update errors; invalidation will refetch
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.institutes.lists(),
      });
    },
  });
};

export const useImportInstitutes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileData) => {
      const res = await instituteService.importInstitutes(fileData);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.institutes.lists(),
      });
    },
  });
};

export const useExportInstitutes = () => {
  return useMutation({
    mutationFn: async (params) => {
      const res = await instituteService.exportInstitutes(params);
      return res.data;
    },
  });
};

export const useDownloadCsvTemplate = () => {
  return useMutation({
    mutationFn: async () => {
      const res = await instituteService.downloadCsvTemplate();
      return res.data;
    },
  });
};


